import React, { useState } from "react";
import { Card, CardContent, Typography, Box, Divider, Grid, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { MatxLayoutSettings } from "app/components/MatxLayout/settings";
import { formatCurrency } from "../utils/dashboardUtils";

const PaymentSummary = ({ summaryData, onReviewPayments }) => {
  const [offlineExpanded, setOfflineExpanded] = useState(false);
  const [onlineExpanded, setOnlineExpanded] = useState(false);
  const [vendorPosExpanded, setVendorPosExpanded] = useState(false);

  const countItems = [
    { label: "Fully Paid", value: summaryData?.fully_paid_count || 0 },
    { label: "Partially Paid", value: summaryData?.partially_paid_count || 0, hasReview: true },
    { label: "Zero Paid", value: summaryData?.zero_paid_count || 0 },
    { label: "Total Unpaid Guests", value: summaryData?.unpaid_guest_count || 0 },
    { label: "Total Round Table Guests", value: summaryData?.paid_guest_count || 0 }
  ];

  const offlineTotal = (summaryData?.total_receipt_payment || 0) + (summaryData?.total_cash_payment || 0);
  const onlineTotal = (summaryData?.total_razorpay_payment || 0) + (summaryData?.total_link_payment || 0);
  const vendorPosTotal = summaryData?.total_vendor_pos_payment || 0;
  const balanceAmount = (summaryData?.total_amount || 0) - (summaryData?.total_amount_paid || 0);

  const renderItem = (item, idx) => (
    <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" sx={{ gap: 1, flexWrap: "nowrap" }}>
      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight="500" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.label}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        <Typography variant="body1" fontWeight="700">{item.value}</Typography>
      </Box>
    </Box>
  );

  return (
    <Card sx={{ borderRadius: "12px", border: `1px solid ${MatxLayoutSettings.palette.borderGrey}`, boxShadow: "none", mt: 4 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" fontWeight="700" gutterBottom>Payment Summary</Typography>
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" fontWeight="700" color={MatxLayoutSettings.palette.black} sx={{ textTransform: "uppercase" }}>
            TOTAL PAYMENT RECEIVED
          </Typography>
          <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5, color: MatxLayoutSettings.palette.active, fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
            {formatCurrency(summaryData?.total_amount_paid)}
          </Typography>
        </Box>
        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={{ xs: 2, md: 8 }}>
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {countItems.map(renderItem)}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {/* Offline Payments */}
              <Box sx={{ borderLeft: `3px solid ${MatxLayoutSettings.palette.active}`, pl: 2, py: 0.5 }}>
                <Box
                  onClick={() => setOfflineExpanded(!offlineExpanded)}
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: MatxLayoutSettings.palette.black }}>
                      Offline Payments
                    </Typography>
                    <Typography variant="caption" color={MatxLayoutSettings.palette.textBlack} fontWeight="600">
                      Total Amount:{" "}
                      <Box component="span" sx={{ color: MatxLayoutSettings.palette.black, fontWeight: "800" }}>
                        {formatCurrency(offlineTotal)}
                      </Box>
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ mt: -0.5, pointerEvents: "none" }}>
                    {offlineExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={offlineExpanded} timeout="auto" unmountOnExit>
                  <Box display="flex" flexDirection="column" gap={1.5} sx={{ mt: 1.5 }}>
                    {renderItem({ label: "Direct to Account", value: formatCurrency(summaryData?.total_receipt_payment || 0) }, "receipt")}
                    {renderItem({ label: "Cash Payments", value: formatCurrency(summaryData?.total_cash_payment || 0) }, "cash")}
                  </Box>
                </Collapse>
              </Box>

              {/* Online Payments */}
              <Box sx={{ borderLeft: `3px solid ${MatxLayoutSettings.palette.active}`, pl: 2, py: 0.5 }}>
                <Box
                  onClick={() => setOnlineExpanded(!onlineExpanded)}
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: MatxLayoutSettings.palette.black }}>
                      Online Payments
                    </Typography>
                    <Typography variant="caption" color={MatxLayoutSettings.palette.textBlack} fontWeight="600">
                      Total Amount:{" "}
                      <Box component="span" sx={{ color: MatxLayoutSettings.palette.black, fontWeight: "800" }}>
                        {formatCurrency(onlineTotal)}
                      </Box>
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ mt: -0.5, pointerEvents: "none" }}>
                    {onlineExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={onlineExpanded} timeout="auto" unmountOnExit>
                  <Box display="flex" flexDirection="column" gap={1.5} sx={{ mt: 1.5 }}>
                    {renderItem({ label: "Razorpay Payments", value: formatCurrency(summaryData?.total_razorpay_payment || 0) }, "razorpay")}
                    {renderItem({ label: "Link Payments", value: formatCurrency(summaryData?.total_link_payment || 0) }, "link")}
                  </Box>
                </Collapse>
              </Box>

              {/* Vendor POS Payments */}
              {summaryData?.vendor_pos_payments?.length > 0 && (
                <Box sx={{ borderLeft: `3px solid ${MatxLayoutSettings.palette.active}`, pl: 2, py: 0.5 }}>
                  <Box
                    onClick={() => setVendorPosExpanded(!vendorPosExpanded)}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight="700" sx={{ color: MatxLayoutSettings.palette.black }}>
                        Vendor POS Payments
                      </Typography>
                      <Typography variant="caption" color={MatxLayoutSettings.palette.textBlack} fontWeight="600">
                        Total Amount:{" "}
                        <Box component="span" sx={{ color: MatxLayoutSettings.palette.black, fontWeight: "800" }}>
                          {formatCurrency(vendorPosTotal)}
                        </Box>
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ mt: -0.5, pointerEvents: "none" }}>
                      {vendorPosExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                  <Collapse in={vendorPosExpanded} timeout="auto" unmountOnExit>
                    <Box display="flex" flexDirection="column" gap={1.5} sx={{ mt: 1.5 }}>
                      {summaryData?.vendor_pos_payments?.map((vendor) => (
                        <Box key={vendor.pos_vendor_id}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ gap: 1, flexWrap: "nowrap" }}>
                            <Box display="flex" flexDirection="column" sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" fontWeight="600" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {vendor.vendor_name}
                              </Typography>
                              <Typography variant="caption" color={MatxLayoutSettings.palette.textBlack} sx={{ opacity: 0.8 }}>
                                {vendor.pos_name}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="700" sx={{ flexShrink: 0 }}>
                              {formatCurrency(vendor.total_vendor_pos_payment)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Balance */}
              <Box sx={{ mt: 1 }}>
                {renderItem({ label: "Balance to be Received", value: formatCurrency(balanceAmount) }, "balance")}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PaymentSummary;
